import { useEffect, useState } from "react";
import api, { errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, Trash2, Pencil, Key } from "lucide-react";
import { toast } from "sonner";
import { useAuth, hasRole } from "@/lib/auth";
import { formatDate } from "@/lib/format";

export default function SystemDashboardPage() {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ 
    organization_name: "", admin_name: "", admin_email: "", method: "link", password: "",
    state_code: "", gstin: "", pan: "", address: "", email: "", phone: ""
  });
  const [editForm, setEditForm] = useState({ 
    id: "", organization_name: "",
    state_code: "", gstin: "", pan: "", address: "", email: "", phone: ""
  });
  const [resetForm, setResetForm] = useState({ id: "", method: "link", password: "" });

  const load = () => api.get("/system/organizations").then(({ data }) => setOrgs(data));
  useEffect(() => { load(); }, []);

  const createOrg = async () => {
    setBusy(true);
    try {
      await api.post("/system/organizations", form);
      toast.success(`Organization ${form.organization_name} created`);
      setOpen(false);
      setForm({ 
        organization_name: "", admin_name: "", admin_email: "", method: "link", password: "",
        state_code: "", gstin: "", pan: "", address: "", email: "", phone: ""
      });
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Failed to create organization"));
    } finally {
      setBusy(false);
    }
  };

  const updateOrg = async () => {
    setBusy(true);
    try {
      await api.patch(`/system/organizations/${editForm.id}`, { 
        organization_name: editForm.organization_name,
        state_code: editForm.state_code,
        gstin: editForm.gstin,
        pan: editForm.pan,
        address: editForm.address,
        email: editForm.email,
        phone: editForm.phone
      });
      toast.success("Organization updated");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Failed to update organization"));
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (o) => {
    setEditForm({ 
      id: o.id, 
      organization_name: o.name,
      state_code: o.state_code || "",
      gstin: o.gstin || "",
      pan: o.pan || "",
      address: o.address || "",
      email: o.email || "",
      phone: o.phone || ""
    });
    setEditOpen(true);
  };

  const openReset = (o) => {
    setResetForm({ id: o.id, method: "link", password: "" });
    setResetOpen(true);
  };

  const handleResetPassword = async () => {
    setBusy(true);
    try {
      await api.post(`/system/organizations/${resetForm.id}/reset-admin-password`, {
        method: resetForm.method,
        password: resetForm.password
      });
      toast.success(resetForm.method === "link" ? "Reset link sent to admin" : "Password updated successfully");
      setResetOpen(false);
    } catch (e) {
      toast.error(errorMessage(e, "Failed to update admin access"));
    } finally {
      setBusy(false);
    }
  };

  const delOrg = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete the organization '${name}'? This will also remove all its users.`)) return;
    try {
      await api.delete(`/system/organizations/${id}`);
      toast.success("Organization deleted");
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Failed to delete organization"));
    }
  };

  const canManage = hasRole(user, "system");

  if (!canManage) {
    return <div className="text-center p-8 text-muted-foreground">Access denied.</div>;
  }

  return (
    <div className="space-y-4" data-testid="system-dashboard-page">
      <div className="flex items-center gap-3">
        <p className="text-sm text-muted-foreground flex-1">System Administration: Manage all tenant organizations.</p>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="new-org-btn">
          <Plus size={13} className="mr-1.5" /> Add Organization
        </Button>
      </div>

      <div className="border border-border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-b border-border">
            <tr className="text-left">
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground">Organization Name</th>
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground">Slug</th>
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground">Created</th>
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {orgs.length === 0 ? (
              <tr><td colSpan="4" className="text-center py-4 text-muted-foreground text-xs">No tenant organizations found.</td></tr>
            ) : orgs.map((o) => (
              <tr key={o.id} className="border-b border-border hover:bg-muted/40" data-testid={`org-row-${o.slug}`}>
                <td className="px-3 py-2.5 font-medium">{o.name}</td>
                <td className="px-3 py-2.5 text-xs font-mono text-muted-foreground">{o.slug}</td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground font-mono">{formatDate(o.created_at, "dd MMM yy")}</td>
                <td className="px-3 py-2.5 text-right space-x-1">
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground" onClick={() => openReset(o)} data-testid={`org-key-${o.slug}`} title="Manage Admin Password">
                    <Key size={13} />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground" onClick={() => openEdit(o)} data-testid={`org-edit-${o.slug}`}>
                    <Pencil size={13} />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => delOrg(o.id, o.name)} data-testid={`org-delete-${o.slug}`}>
                    <Trash2 size={13} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="new-org-dialog">
          <DialogHeader className="shrink-0">
            <DialogTitle>Add Organization</DialogTitle>
            <DialogDescription>Create a new tenant organization. The initial admin will receive an email to set their password.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 flex-1 overflow-y-auto px-1 -mx-1 pr-2 pb-4">
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs">Organization Name <span className="text-destructive">*</span></Label>
                <Input value={form.organization_name} onChange={(e) => setForm({ ...form, organization_name: e.target.value })} data-testid="new-org-name" placeholder="Acme Corp" maxLength={255} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Initial Admin Name</Label>
                <Input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} data-testid="new-admin-name" placeholder="Jane Doe" maxLength={255} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Initial Admin Email <span className="text-destructive">*</span></Label>
                <Input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} placeholder="admin@acmecorp.com" data-testid="new-admin-email" maxLength={255} />
              </div>
            </div>
            
            <div className="pt-2 border-t border-border space-y-3">
              <p className="text-xs font-medium text-muted-foreground">Optional Organization Details</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">GSTIN</Label>
                  <Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} placeholder="22AAAAA0000A1Z5" maxLength={32} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">PAN</Label>
                  <Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value })} placeholder="AAAAA0000A" maxLength={16} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">State Code</Label>
                  <Input value={form.state_code} onChange={(e) => setForm({ ...form, state_code: e.target.value })} placeholder="22" maxLength={8} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91..." maxLength={32} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Contact Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="info@acmecorp.com" maxLength={255} />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs">Address</Label>
                  <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="123 Main St..." maxLength={512} />
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-border">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Admin Password Setup <span className="text-destructive">*</span></Label>
                  <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="link">Send Reset Password Link</SelectItem>
                      <SelectItem value="manual">Set Password Manually</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {form.method === "manual" && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Admin Password <span className="text-destructive">*</span></Label>
                    <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Minimum 8 characters" />
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter className="shrink-0 pt-4 mt-2 border-t border-border/50">
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={createOrg} disabled={!form.organization_name || !form.admin_email || busy || (form.method === "manual" && form.password.length < 8)} data-testid="new-org-submit">
              {busy && <Loader2 size={13} className="animate-spin mr-1.5" />}
              <Plus size={13} className="mr-1.5" /> Create Organization
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="edit-org-dialog">
          <DialogHeader className="shrink-0">
            <DialogTitle>Edit Organization</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 flex-1 overflow-y-auto px-1 -mx-1 pr-2 pb-4">
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs">Organization Name <span className="text-destructive">*</span></Label>
                <Input value={editForm.organization_name} onChange={(e) => setEditForm({ ...editForm, organization_name: e.target.value })} data-testid="edit-org-name" maxLength={255} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">GSTIN</Label>
                <Input value={editForm.gstin} onChange={(e) => setEditForm({ ...editForm, gstin: e.target.value })} maxLength={32} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">PAN</Label>
                <Input value={editForm.pan} onChange={(e) => setEditForm({ ...editForm, pan: e.target.value })} maxLength={16} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">State Code</Label>
                <Input value={editForm.state_code} onChange={(e) => setEditForm({ ...editForm, state_code: e.target.value })} maxLength={8} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Phone</Label>
                <Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} maxLength={32} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Contact Email</Label>
                <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} maxLength={255} />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs">Address</Label>
                <Input value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} maxLength={512} />
              </div>
            </div>
            
          </div>
          <DialogFooter className="shrink-0 pt-4 mt-2 border-t border-border/50">
            <Button variant="ghost" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={updateOrg} disabled={!editForm.organization_name || busy} data-testid="edit-org-submit">
              {busy && <Loader2 size={13} className="animate-spin mr-1.5" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="max-w-md" data-testid="reset-admin-dialog">
          <DialogHeader>
            <DialogTitle>Manage Admin Password</DialogTitle>
            <DialogDescription>Reset the primary admin's password for this organization.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Action <span className="text-destructive">*</span></Label>
              <Select value={resetForm.method} onValueChange={(v) => setResetForm({ ...resetForm, method: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="link">Send Reset Password Link</SelectItem>
                  <SelectItem value="manual">Set Password Manually</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {resetForm.method === "manual" && (
              <div className="space-y-1.5">
                <Label className="text-xs">New Password <span className="text-destructive">*</span></Label>
                <Input type="password" value={resetForm.password} onChange={(e) => setResetForm({ ...resetForm, password: e.target.value })} placeholder="Minimum 8 characters" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResetOpen(false)}>Cancel</Button>
            <Button onClick={handleResetPassword} disabled={busy || (resetForm.method === "manual" && resetForm.password.length < 8)} data-testid="reset-admin-submit">
              {busy && <Loader2 size={13} className="animate-spin mr-1.5" />}
              {resetForm.method === "link" ? "Send Link" : "Update Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
